import { RequirePermission } from "@components/common/require-permission";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { BaseProjectProps } from "@custom-types/base-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { ActionButtonSx } from "@styles/common-styles";
import {
  FaroCoreApiConst,
  FaroCoreApiTypes,
  FaroCoreApiUtils,
} from "@stellar/core-api-client";
import { updateProject } from "@store/project/project-thunks";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import {
  Button,
  useToast,
  Dropdown,
  Dialog,
  TextField,
  FontWeights,
} from "@flat-ui";
import { Grid } from "@mui-wrapper";
import { HelpIconTooltip } from "@components/common/help-icon-tooltip";
import { projectStateSelector } from "@store/project/project-slice";

/**
 * Get the allowed options in archiving state dropdown based on current state
 * The following are the rules for allowed state changes:
 * Unarchived -> Archived
 * Archived -> Unarchived, Ready to Download
 * Ready to Download -> Unarchived, Archived, Downloaded
 * Downloaded -> Archived, Ready to Download
 */
const ALLOWED_ARCHIVING_STATE_TRANSITIONS: Record<
  FaroCoreApiTypes.ArchivingState,
  FaroCoreApiTypes.ArchivingState[]
> = {
  UNARCHIVED: ["ARCHIVED"],
  ARCHIVED: ["UNARCHIVED", "ARCHIVED_READY_TO_DOWNLOAD"],
  ["ARCHIVED_READY_TO_DOWNLOAD"]: [
    "UNARCHIVED",
    "ARCHIVED",
    "ARCHIVED_DOWNLOADED",
  ],
  ["ARCHIVED_DOWNLOADED"]: ["ARCHIVED", "ARCHIVED_READY_TO_DOWNLOAD"],
};

/** A component for editing project details */
export function EditProject({ project }: BaseProjectProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [projectName, setProjectName] = useState<string>(project.name);
  const [projectAccessLevel, setProjectAccessLevel] =
    useState<FaroCoreApiTypes.ProjectAccessLevel>(project.accessLevel);
  const [projectState, setProjectState] =
    useState<FaroCoreApiTypes.ArchivingState>(project.state);
  const [projectArea, setProjectArea] = useState<
    FaroCoreApiTypes.ProjectArea["amount"]
  >(project.area.amount);

  const { fetchingProject } = useAppSelector(projectStateSelector);

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const dispatch = useAppDispatch();
  const { openToast } = useToast();

  function onCloseDialog(): void {
    setIsDialogOpen(false);
    setProjectName(project.name);
    setProjectAccessLevel(project.accessLevel);
    setProjectState(project.state);
    setProjectArea(project.area.amount);
  }

  async function onUpdateProjectDetails(): Promise<void> {
    trackEvent({
      name: ProjectEvents.editProjectDetails,
      props: {
        isNameChanged: projectName.trim() !== project.name.trim(),
        nameLength: projectName.trim().length,
        isAccessLevelChanged: projectAccessLevel !== project.accessLevel,
        isArchivingStateChanged: projectState !== project.state,
        isProjectAreaChanged: projectArea !== project.area.amount,
        ...(projectAccessLevel !== project.accessLevel && {
          oldAccessLevel: project.accessLevel,
          newAccessLevel: projectAccessLevel,
        }),
        ...(projectState !== project.state && {
          oldArchivingState: project.state,
          newArchivingState: projectState,
        }),
        ...(projectArea !== project.area.amount && {
          oldProjectArea: project.area.amount,
          newProjectArea: projectArea,
        }),
      },
    });

    try {
      await dispatch(
        updateProject({
          projectId: project.id,
          payload: {
            name: projectName.trim(),
            accessLevel: projectAccessLevel,
            archivingState: projectState,
            sqft: projectArea,
          },
        })
      ).unwrap();
      openToast({
        title: "Project details updated successfully!",
      });
      setIsDialogOpen(false);
    } catch (error) {
      handleErrorWithToast({
        id: `edit-project-details-${Date.now().toString()}`,
        title: "Failed to update project details",
        error,
      });
    }
  }

  // TODO: Support sqm as well after implementation of https://faro01.atlassian.net/browse/ST-4363
  // All projects should be in sqft already. However, since backend api only covers sqft, we keep this check for safety
  const shouldDisableEditButton = project.area.unit !== "sqft";

  /** Whether there are any changes in project details dialog */
  const hasNoChanges =
    projectName.trim() === project.name.trim() &&
    projectAccessLevel === project.accessLevel &&
    projectState === project.state &&
    projectArea === project.area.amount;

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          ...ActionButtonSx,
          alignItems: "center",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <RequirePermission permission="project.edit">
          <Button
            sx={{ ...ActionButtonSx }}
            onClick={() => {
              trackEvent({ name: ProjectEvents.openEditProjectDetails });
              setIsDialogOpen(true);
            }}
            variant="secondary"
            disabled={shouldDisableEditButton}
          >
            Edit Project Details
          </Button>
        </RequirePermission>

        <HelpIconTooltip title="This feature only supports projects with area unit in sqft" />
      </Grid>

      <Dialog
        open={isDialogOpen}
        title={
          <TextWithTooltip
            prefix="Edit"
            text={project.name}
            suffix="details"
            customSx={{ fontWeight: FontWeights.Bold }}
          />
        }
        onCancel={onCloseDialog}
        onConfirm={onUpdateProjectDetails}
        isConfirmDisabled={hasNoChanges}
        confirmTooltip={hasNoChanges ? "No changes to save" : undefined}
        showSpinner={fetchingProject.updatingProject === "pending"}
      >
        <TextField
          label="Project ID"
          text={project.id}
          disabled={true}
          onTextChanged={() => {}}
          fullWidth
          controlStyle={{ mt: 2 }}
        />

        <TextField
          label="Project name"
          text={projectName}
          onTextChanged={setProjectName}
          controlStyle={{ my: 2 }}
          fullWidth
        />

        <Dropdown
          fullWidth
          value={projectAccessLevel}
          onChange={(event) => setProjectAccessLevel(event.target.value)}
          variant="outlined"
          label="Access Level"
          options={FaroCoreApiConst.allProjectAccessLevel.map(
            (accessLevel) => ({
              key: accessLevel,
              value: accessLevel,
              label:
                FaroCoreApiUtils.Project.AccessLevelDisplayNames[accessLevel],
            })
          )}
          sx={{ mb: 2 }}
        />

        <Dropdown
          fullWidth
          value={projectState}
          onChange={(event) => setProjectState(event.target.value)}
          label="Archiving State"
          options={FaroCoreApiConst.allArchivingState.map((state) => ({
            key: state,
            value: state,
            label: FaroCoreApiUtils.Project.StateDisplayNames[state],
            isDisabled:
              !ALLOWED_ARCHIVING_STATE_TRANSITIONS[projectState].includes(
                state
              ),
          }))}
        />

        <TextField
          type="number"
          label={`Project Area (${project.area.unit})`}
          text={String(projectArea)}
          onTextChanged={(newLimitValue: string): void => {
            setProjectArea(Number(newLimitValue));
          }}
          fullWidth
          controlStyle={{ mt: 2 }}
        />
      </Dialog>
    </>
  );
}
